import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';

import { store, factory as splitFactory } from 'src/main';
import { IBrowserClient } from '@splitsoftware/splitio/types/splitio';
import { encodeHash } from 'src/utils/hashIds';
import {
  STACKLINE_SUPER_USER_VALIDATOR_REGEX,
  STACKLINE_USER_VALIDATOR_REGEX,
  STACKLINE_DEPLOYMENT_USER_VALIDATOR_REGEX
} from 'src/utils/validators';
import ReduxStore from 'src/types/store/reduxStore';
import { createSelector } from 'reselect';

// Shared Beacon Split.io flag; the `feature` attribute selects the specific gate
// (same flag app.ts uses for ForceBeaconPro / AdvertisingRedirect, etc.).
const BEACON_SPLIT_IO_TREATMENT = 'beacon-pro-features-by-retailer-id';

export function addHashIdToUserConfig(config: {
  data: {
    userConfig: ReduxStore['user']['config'];
    session: ReduxStore['user']['session'];
  };
}) {
  const { userConfig } = config.data;
  // Add a hashId to the userConfig.vendor object and return the entire config
  const clonedConfig = _cloneDeep(config);
  if (clonedConfig.data && clonedConfig.data.userConfig) {
    clonedConfig.data.userConfig.isStacklineDeploymentSuperUser =
      clonedConfig.data &&
      clonedConfig.data.session &&
      STACKLINE_DEPLOYMENT_USER_VALIDATOR_REGEX.test(clonedConfig.data.session.email);
    clonedConfig.data.userConfig.isStacklineSuperUser =
      clonedConfig.data &&
      clonedConfig.data.session &&
      STACKLINE_SUPER_USER_VALIDATOR_REGEX.test(clonedConfig.data.session.email);
    clonedConfig.data.userConfig.isStacklineUser =
      clonedConfig.data &&
      clonedConfig.data.session &&
      STACKLINE_USER_VALIDATOR_REGEX.test(clonedConfig.data.session.email);
  }

  if (userConfig && userConfig.vendor && userConfig.vendor.BeaconClientId !== -1) {
    const { BeaconClientId } = userConfig.vendor;
    const hashId = encodeHash(BeaconClientId);
    clonedConfig.data.userConfig.vendor.hashId = hashId;
    return clonedConfig;
  }

  return clonedConfig;
}

export const onPrimeMode = (
  { retailer, user, app }: Pick<ReduxStore, 'retailer' | 'user' | 'app'> = store.getState()
) => {
  const { isRestrictedDemoUser, isStacklineUser, isStacklineSuperUser } = user.config && user.config;
  if (isRestrictedDemoUser) {
    return false;
  }
  if (
    !isStacklineSuperUser &&
    retailer.dataTypesAvailable.indexOf('traffic') >= 0 &&
    isStacklineUser &&
    app.stage === 'dev'
  ) {
    return true;
  }

  const subscriptionsByRetailers = _get(user, ['config', 'subscriptionsByRetailers'], {} as {});
  // "shopperbehavior" is enabled and retailer has to be (Amazon US any stage) or (Amazon UK only on dev)
  const enableBeaconPrimeMode =
    subscriptionsByRetailers &&
    subscriptionsByRetailers[retailer.id] &&
    subscriptionsByRetailers[retailer.id].PrimeSubscription &&
    subscriptionsByRetailers[retailer.id].PrimeSubscription === 'true';
  return enableBeaconPrimeMode;
};

export const showContentAndRatings = () => {
  const { retailer } = store.getState();
  const contentEnabledRetailers = new Set([
    '1',
    '2',
    '3',
    '4',
    '6',
    '11',
    '14',
    '15',
    '16',
    '17',
    '18',
    '28',
    '29',
    '30',
    '31',
    '32',
    '33',
    '53',
    '62',
    '72',
    '74',
    '1493'
  ]);
  if (contentEnabledRetailers.has(retailer.id)) {
    return true;
  }
  return false;
};

export const onAmazonUS = ({ retailer }: Pick<ReduxStore, 'retailer'> = store.getState()) => retailer.id === '1';

/**
 * Redux selector that returns the email address of the currently signed in user
 */
export const curUserEmailSelector = createSelector(
  (state: ReduxStore) => state.user.session.email,
  (email) => email
);

/**
 * Redux selector that returns the email address of the currently signed in user
 */
export const curUserIdSelector = createSelector(
  (state: ReduxStore) => state.user.session.userId,
  (userId) => userId
);

/**
 * Redux selector that returns true if the user is a Pulse user, meaning they
 * have readonly access to Drive.
 */
export const isAdAuditUserSelector = createSelector(
  (state: ReduxStore) => _get(state, ['user', 'config', 'adAuditEnabled'], false),
  (isAuditUser) => isAuditUser
);

export const onDisplayAdvertisingMode = (
  { retailer, user, app }: Pick<ReduxStore, 'retailer' | 'user' | 'app'> = store.getState()
) => {
  const { isRestrictedDemoUser, isStacklineUser, isStacklineSuperUser } = user.config && user.config;

  if (isRestrictedDemoUser) {
    return false;
  }
  if (
    !isStacklineSuperUser &&
    retailer.dataTypesAvailable.indexOf('displayAdvertising') >= 0 &&
    isStacklineUser &&
    app.stage === 'dev'
  ) {
    return true;
  }
  const modulesSubscribed = _get(user, ['config', 'modulesSubscribed'], {} as {});
  const enableDisplayAdvertisingMode =
    modulesSubscribed && modulesSubscribed.indexOf('displayadvertising') >= 0 && retailer.id === '1';
  return enableDisplayAdvertisingMode;
};

export const onChargebacksMode = (
  { retailer, user }: Pick<ReduxStore, 'retailer' | 'user' | 'app'> = store.getState()
) => {
  const { isRestrictedDemoUser } = user.config && user.config;
  if (isRestrictedDemoUser) {
    return false;
  }
  const modulesSubscribed: string[] = _get(user, ['config', 'modulesSubscribed'], []);
  const subscribedToChargebacks = modulesSubscribed.indexOf('chargebacks') >= 0;
  const retailerEnabledForChargebacks =
    _get(user, ['config', 'subscriptionsByRetailers', retailer.id, 'chargebacks']) === 'true';
  if (subscribedToChargebacks && retailerEnabledForChargebacks) {
    return true;
  }
  // Split.io dark-launch override: flip chargebacks on per client/retailer via the shared beacon
  // split (feature: 'ChargebacksEnabled') without a subscriptionConfig change. getTreatment returns
  // 'control' (=> off) until the SDK is ready, so this is safe to call during render.
  const splitClient: IBrowserClient = splitFactory.client();
  const beaconClientId = _get(user, ['config', 'vendor', 'BeaconClientId']);
  const userEmail = _get(user, ['session', 'email'], '');
  return (
    splitClient.getTreatment(BEACON_SPLIT_IO_TREATMENT, {
      feature: 'ChargebacksEnabled',
      retailerId: retailer.id,
      beaconClientId,
      userEmail
    }) === 'on'
  );
};

export const onContentGenerationMode = (
  { retailer, user }: Pick<ReduxStore, 'retailer' | 'user' | 'app'> = store.getState()
) => {
  const { isRestrictedDemoUser } = user.config && user.config;
  if (isRestrictedDemoUser) {
    return false;
  }
  const modulesSubscribed: string[] = _get(user, ['config', 'modulesSubscribed'], []);
  const subscribedToContentGeneration = modulesSubscribed.indexOf('contentgeneration') >= 0;
  const retailerEnabledForContentGeneration =
    _get(user, ['config', 'subscriptionsByRetailers', retailer.id, 'contentgeneration']) === 'true';
  return subscribedToContentGeneration && retailerEnabledForContentGeneration;
};

export const onShortagesMode = (
  { retailer, user }: Pick<ReduxStore, 'retailer' | 'user' | 'app'> = store.getState()
) => {
  const modulesSubscribed: string[] = _get(user, ['config', 'modulesSubscribed'], []);
  const enableShortagesMode =
    modulesSubscribed && modulesSubscribed.indexOf('shortagedisputes') >= 0 && ['1', '11'].includes(retailer.id); // Enabled for Amazon US & Amazon Canada only

  return enableShortagesMode;
};

export const onBasicAdvertisingMode = (
  { retailer, user, app }: Pick<ReduxStore, 'retailer' | 'user' | 'app'> = store.getState()
) => {
  const { isRestrictedDemoUser, isStacklineUser, isStacklineSuperUser } = user.config && user.config;
  if (isRestrictedDemoUser) {
    return false;
  }
  if (
    !isStacklineSuperUser &&
    retailer.dataTypesAvailable.indexOf('displayAdvertising') >= 0 &&
    isStacklineUser &&
    app.stage === 'dev'
  ) {
    return true;
  }
  const modulesSubscribed = _get(user, ['config', 'modulesSubscribed'], {} as {});
  const basicAdvertisingMode =
    modulesSubscribed.indexOf('displayadvertising') < 0 ||
    (modulesSubscribed.indexOf('displayadvertising') >= 0 && retailer.id !== '1');
  return basicAdvertisingMode;
};
